import { Notification } from "@drone-js/core";
import type { PropertyData } from "libs/codegen/property-onboarding";
import { Fragment, type FunctionComponent, useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { pictureTypeMapper } from "./constants";
import { DropGrid } from "./upload/DropGrid";

interface Props {
    fieldPrefix?: string;
    roomPhotos?: boolean;
}

export const PhotosController: FunctionComponent<Props> = ({ fieldPrefix, roomPhotos }) => {
    const { control, formState, getFieldState } = useFormContext<PropertyData>();
    const {
        cmsData: {
            photo: { addAtLeast3PhotosToContinue },
        },
    } = useStaticDataAtomValue();

    const targetField = `${fieldPrefix ? fieldPrefix + "." : ""}photos`;
    const fieldArray = useFieldArray({
        control,
        name: targetField as "photos",
        rules: roomPhotos
            ? undefined
            : {
                  validate: {
                      atLeastThree: (v) => v.length >= 3 || addAtLeast3PhotosToContinue,
                      mainPhoto: (v) =>
                          v.some((p) => p.pictureTypeId === pictureTypeMapper.mainPhoto) || "Main photo is required",
                  },
              },
    });
    const { setValue } = useFormContext<PropertyData>();
    const { error } = getFieldState(targetField as "photos", formState);

    useEffect(() => {
        // if every photo is not a main photo, then set the first one to main
        if (
            !roomPhotos &&
            fieldArray.fields.length > 0 &&
            fieldArray.fields.every((item) => item.pictureTypeId !== pictureTypeMapper.mainPhoto)
        )
            setValue(`${targetField}.0.pictureTypeId` as "photos.0.pictureTypeId", pictureTypeMapper.mainPhoto);
    }, [fieldArray.fields, roomPhotos]);

    return (
        <Fragment>
            {(error || fieldArray.fields.length < 3) && (
                <Notification
                    intent={error ? "error" : "notice"}
                    title={error ? error.root.message : addAtLeast3PhotosToContinue}
                    asset={{ id: error ? "fill.symbol.triangle-exclamation" : "fill.symbol.circle-info" }}
                />
            )}
            <DropGrid disableMainPhoto={roomPhotos} targetField={targetField} {...fieldArray} />
        </Fragment>
    );
};
